import React from 'react';
import { Dropdown } from './Dropdown';

// BulkActionBar - the selection strip above a list table (UI/UX hand-off W-L7).
//
// Routine Work's "1 task selected / Bulk Actions" bar used to appear INSIDE the table box, with no
// left padding, and inside the table's scroll area - so selecting the first row inserted a bar
// above it and every row jumped down. This strip sits in the list body ABOVE the table's scroll
// box (never inside it), has the standard padding, and is ALWAYS rendered at the same fixed
// height: with nothing selected it says so and the menu is disabled. Selecting rows therefore
// never moves the list.

export interface BulkAction {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  hidden?: boolean;
}

export interface BulkActionBarProps {
  /** Number of selected rows. */
  count: number;
  /** Singular noun for the rows, e.g. "task" -> "3 tasks selected". */
  noun?: string;
  actions: BulkAction[];
  /** Clears the selection; shown as a link while something is selected. */
  onClearSelection?: () => void;
  /** Text while nothing is selected. */
  emptyText?: string;
  className?: string;
}

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  count,
  noun = 'row',
  actions,
  onClearSelection,
  emptyText,
  className = '',
}) => {
  const visible = actions.filter((a) => !a.hidden);
  const plural = count === 1 ? noun : `${noun}s`;
  return (
    <div
      data-slot="bulk-action-bar"
      role="region"
      aria-label="Bulk actions"
      className={`tw-mb-2 tw-flex tw-h-11 tw-shrink-0 tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-px-3 ${className}`}
    >
      <span aria-live="polite" className={`tw-truncate tw-text-sm ${count > 0 ? 'tw-font-medium tw-text-content' : 'tw-text-content-muted'}`}>
        {count > 0 ? `${count} ${plural} selected` : emptyText ?? `Select ${noun}s for bulk actions`}
      </span>
      {count > 0 && onClearSelection && (
        <button
          type="button"
          onClick={onClearSelection}
          className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary hover:tw-underline"
        >
          Clear Selection
        </button>
      )}
      <div className="tw-ml-auto">
        <Dropdown align="end">
          <Dropdown.Toggle
            className="tw-text-sm disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
            disabled={count === 0 || visible.length === 0}
          >
            Bulk Actions
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {visible.map((a) => (
              <Dropdown.Item key={a.key} onClick={a.onClick} disabled={a.disabled}>
                {a.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};

export default BulkActionBar;
